import { Component, EventEmitter, Input, OnInit, Output, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Observable, of } from 'rxjs';
import { LoginCredentials } from './models/login-config.model';
import { BodyBackgroundService } from '../../services/body-background.service';

@Component({
  selector: 'lib-design-system-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})
export class DesignSystemLoginComponent implements OnInit, OnChanges, OnDestroy {
  @Input() loginTitle: string = '';
  @Input() showInfoBanner: boolean = false;
  @Input() infoBannerText?: string;
  @Input() infoBannerLinkText?: string;
  @Input() infoBannerLinkUrl?: string;
  @Input() infoBannerIcon?: string;
  @Input() showFooterCta: boolean = false;
  @Input() footerCtaIcon?: string;
  @Input() footerCtaText?: string;
  @Input() footerCtaLinkText?: string;
  @Input() footerCtaLinkUrl?: string;
  @Input() forgotPasswordUrl?: string;
  @Input() applicationId: string = '';
  @Input() isLoading$: Observable<boolean> = of(false);
  @Input() error$: Observable<string | null> = of(null);
  @Input() backgroundColor?: string;
  @Input() logoType: 'white' | 'color' = 'color';

  @Output() loginSubmit = new EventEmitter<LoginCredentials>();
  @Output() errorDismiss = new EventEmitter<void>();

  loginForm!: FormGroup;
  submitted = false;

  constructor(
    private formBuilder: FormBuilder,
    private bodyBackgroundService: BodyBackgroundService
  ) {}

  get usernameFieldId(): string {
    return this.backgroundColor ? 'username' : 'username-shelter';
  }

  get passwordFieldId(): string {
    return this.backgroundColor ? 'password' : 'password-shelter';
  }

  ngOnInit(): void {
    this.loginForm = this.formBuilder.group({
      applicationId: [this.applicationId],
      username: [
        '',
        [
          Validators.required,
        ],
      ],
      password: [
        '',
        [
          Validators.required,
        ],
      ],
    });

    // Apply background to body if backgroundColor is provided
    this.bodyBackgroundService.applyBackground(this.backgroundColor);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['backgroundColor']) {
      this.bodyBackgroundService.applyBackground(this.backgroundColor);
    }
  }

  ngOnDestroy(): void {
    // Restore original body background when component is destroyed
    this.bodyBackgroundService.restoreBackground();
  }

  onLogin(): void {
    this.submitted = true;

    if (this.loginForm?.invalid) {
      return;
    }

    const { username, password, applicationId } = this.loginForm.value;
    this.loginSubmit.emit({ username, password, applicationId });
  }

  clearUsernameError(): void {
    this.errorDismiss.emit();
  }

  clearPasswordError(): void {
    this.errorDismiss.emit();
  }

  onDismissError(): void {
    this.errorDismiss.emit();
  }

  isRouterLink(url?: string): boolean {
    if (!url) {
      return false;
    }
    return url.startsWith('/') && !url.startsWith('//');
  }

  isExternalLink(url?: string): boolean {
    if (!url) {
      return false;
    }
    return url.startsWith('http://') || url.startsWith('https://');
  }

  get logoSrc(): string {
    return this.logoType === 'white'
      ? 'assets/images/petszel-logo-vertical-white.svg'
      : 'assets/images/petszel-logo-vertical-color.svg';
  }
}

